export const WEBSITE_V2_PRIMARY_CTA = 'Apply for the AI Revenue Audit';
export const WEBSITE_V2_SECONDARY_CTA = 'See how it works';
export const WEBSITE_V2_WIDGET_LAUNCHER = 'Find Your Best Next Step';
export const WEBSITE_V2_WIDGET_TITLE = 'Website Sales Concierge';

export const websiteV2HeaderLinks = [
  { label: 'How It Works', href: '/software-services#how-it-works' },
  { label: 'Who It Is For', href: '/software-services#who-it-is-for' },
] as const;

export const websiteV2HeroTrustItems = [
  'setup and integration',
  'tuning and governance',
  'ongoing support',
] as const;

export const websiteV2ProblemCards = [
  {
    title: 'Founder dependence',
    body: 'Founder or sales leadership still carries too much of the pipeline.',
  },
  {
    title: 'Inconsistent outbound and follow-up',
    body: 'Good intent exists, but execution rhythm breaks too easily.',
  },
  {
    title: 'Uneven qualification',
    body: 'Too much human time is spent before fit is clear.',
  },
  {
    title: 'Slow pre-sales and proposals',
    body: 'Turnaround is slower than it should be when buyer momentum matters most.',
  },
  {
    title: 'Weak website and inbound flow',
    body: 'The website is not behaving like a real qualification and routing asset.',
  },
  {
    title: 'Fragmented AI execution',
    body: 'Interest is there, but implementation is scattered across tools, people, and experiments.',
  },
] as const;

export const websiteV2WhatWeDoCards = [
  {
    title: 'Reusable foundations',
    body: 'We do not start from scratch every time. Reusable Dopomogai foundations make the first implementation faster, stronger, and easier to govern.',
  },
  {
    title: 'Revenue modules',
    body: 'Website Sales Concierge, qualification workflows, pre-sales support, proposal flow, CRM/reporting, and selected content systems tied to pipeline quality.',
  },
  {
    title: 'Expansion paths',
    body: 'Once the business case is visible, expand into founder content support, proof distribution, handoffs, QA, reporting, and adjacent workflows that protect revenue quality.',
  },
] as const;

export const websiteV2OutcomeColumns = {
  left: {
    title: 'Improve revenue execution',
    body: 'Reduce inconsistency in how new work is captured, qualified, prepared, and moved toward proposal.',
    bullets: [
      'stronger outbound consistency',
      'better qualification discipline',
      'faster pre-sales and proposal turnaround',
      'clearer CRM and reporting',
      'better founder leverage in business development',
      'better use of proof and content in the revenue process',
    ],
  },
  right: {
    title: 'Protect margin and execution quality',
    body: 'Once the revenue system is cleaner, strengthen the workflows that protect delivery quality and leadership control.',
    bullets: [
      'cleaner scoping and handoffs',
      'less coordination drag',
      'better QA rhythm',
      'clearer client-facing reporting',
      'stronger leadership visibility',
    ],
  },
} as const;

export const websiteV2AuditHowItWorks = [
  'Diagnose the main bottleneck.',
  'Map the current revenue workflow.',
  'Review supporting marketing and delivery friction.',
  'Build a conservative ROI hypothesis.',
  'Recommend the best first module or sprint.',
] as const;

export const websiteV2AuditOutputs = [
  'bottleneck map',
  'recommended first module or sprint',
  'conservative ROI model',
  '30/60/90-day roadmap',
  'proposal options',
] as const;

export const websiteV2OfferCards = [
  {
    title: 'AI Revenue Audit',
    subtitle: '5 sessions / 10 business days',
    body: 'Diagnose the bottleneck, build the ROI logic, and decide what should be installed first.',
    highlight: true,
  },
  {
    title: 'Revenue Sprint',
    subtitle: '4–6 weeks',
    body: 'Implement the first revenue system. Typical starting points include the Website Sales Concierge, qualification workflows, proposal support, or CRM/reporting improvements.',
    highlight: false,
  },
  {
    title: 'Revenue + Delivery Pilot',
    subtitle: '8–12 weeks',
    body: 'Strengthen the revenue engine and improve the first delivery workflow that is slowing margin or client experience.',
    highlight: false,
  },
  {
    title: 'Managed AI Workforce',
    subtitle: 'Monthly support',
    body: 'Maintain, tune, and govern the installed systems. Support usage, outreach operations, selected content workflows, and one adjacent improvement track at a time.',
    highlight: false,
  },
] as const;

export const websiteV2FitItems = [
  'Software-services firms selling internationally.',
  'Leadership teams with real workflow pain.',
  'Firms that want implementation, not ideas only.',
  'Teams with a decision-maker and an internal owner.',
] as const;

export const websiteV2NotFitItems = [
  'AI-native consultancies.',
  'Firms looking for free strategy only.',
  'Teams without a real sales process.',
  'Firms without delivery structure.',
  'Companies too early to execute.',
] as const;

export const websiteV2FaqItems = [
  {
    question: 'Is this just a website chatbot?',
    answer: 'No. The Website Sales Concierge is one possible first module. The larger offer is tailored AI revenue systems for software-services firms.',
  },
  {
    question: 'Do you also help with marketing?',
    answer: 'Yes, but in a focused way. We support founder content, proof distribution, repurposing, and selected paid distribution when it directly improves pipeline quality. We are not positioning this as a broad marketing-agency offer.',
  },
  {
    question: 'Do you also help with delivery operations?',
    answer: 'Yes. Delivery and enablement are part of the model. We simply do not lead with them in every conversation because revenue pain is usually easier to prioritize first. Once fit is clear, those workflows become part of the operating system story.',
  },
  {
    question: 'Do we need to use your platform or providers?',
    answer: 'No. There is no mandatory platform license. You can bring your own model or provider, use a managed provider setup with us, or support a self-hosted path where needed.',
  },
  {
    question: 'Is this done-for-you or system installation?',
    answer: 'It can include both implementation and operational support. The core idea is not to replace leadership judgment. It is to install governed systems that reduce manual drag and improve execution quality.',
  },
  {
    question: 'What happens after the audit?',
    answer: 'If the fit is strong, you receive a clear recommendation on the best first module or sprint. That usually means one recommended option, one lower-commitment option, and one broader option.',
  },
] as const;

export const websiteV2WidgetQuickReplies = [
  'We need better lead qualification',
  'Our website is underperforming',
  'We need a broader AI revenue system',
  'I’m not sure where to start',
] as const;

export const websiteV2WidgetRouteSummaries = {
  ai_revenue_audit: {
    summary:
      'Based on what you described, the AI Revenue Audit looks like the clearest next step because the first implementation priority still looks broader than one module.',
    cta: 'Continue to the Audit',
    routeBasis: 'broad_system_pain',
  },
  website_sales_concierge_discussion: {
    summary:
      'Based on what you described, the Website Sales Concierge looks like the clearest first discussion because qualification and inbound workflow seem to be the main bottleneck.',
    cta: 'Discuss the Website Sales Concierge',
    routeBasis: 'website_inbound_pain',
  },
  short_qualification_call: {
    summary:
      'There is likely a fit, but a short qualification call is the cleanest way to confirm the right first path.',
    cta: 'Request a Short Qualification Call',
    routeBasis: 'context_incomplete',
  },
  nurture_not_now: {
    summary:
      'It may be early for an implementation decision right now. We can keep the next step light and reconnect when timing is better.',
    cta: 'Keep It Light for Now',
    routeBasis: 'low_urgency_or_weak_fit',
  },
} as const;

export const websiteV2AuditStepLabels = [
  'Company profile',
  'Contact and authority',
  'Revenue bottleneck',
  'Marketing and proof support',
  'Delivery-readiness',
  'AI context',
  'Urgency and implementation intent',
  'Practical readiness',
] as const;
